<script setup lang="ts">
import { ref, watch } from 'vue'
import { Gamepad2 } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  src: string
  alt?: string
  dimension?: string
  imageDimension?: string
  eager?: boolean
  loadingFallback?: boolean
}>(), {
  alt: '',
  dimension: '1.5rem',
  imageDimension: '1.875rem',
  eager: false,
  loadingFallback: false,
})

const failed = ref(false)
const loaded = ref(false)

watch(() => props.src, () => { failed.value = false; loaded.value = false })
</script>

<template>
  <span
    class="game-art"
    :style="{ '--game-art-size': dimension, '--game-art-image-size': imageDimension }"
    :aria-hidden="alt ? undefined : 'true'"
  >
    <img
      v-if="!failed"
      :class="{ 'image-pending': loadingFallback && !loaded }"
      :src="src"
      :alt="alt"
      :loading="eager ? 'eager' : 'lazy'"
      decoding="async"
      draggable="false"
      @error="failed = true"
      @load="loaded = true"
    />
    <span v-if="failed || (loadingFallback && !loaded)" class="game-art-fallback" aria-hidden="true">
      <slot><Gamepad2 /></slot>
    </span>
  </span>
</template>

<style scoped>
.game-art {
  width: var(--game-art-size);
  height: var(--game-art-size);
  display: inline-grid;
  place-items: center;
  flex: none;
  overflow: hidden;
  aspect-ratio: 1;
}
.game-art img {
  grid-area: 1 / 1;
  display: block;
  width: min(100%, var(--game-art-image-size));
  height: min(100%, var(--game-art-image-size));
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  image-rendering: auto;
}
.game-art-fallback {
  grid-area: 1 / 1;
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: currentColor;
}
.game-art img.image-pending { opacity: 0; }
.game-art-fallback :deep(svg) { width: 58%; height: 58%; }
</style>
